import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import {
  WebsiteSectionContainer,
  WebsiteMarketingCtaButton,
} from '../common/websiteSection';
import cpPreview1 from '../hero/images/cp-preview1.png';
import PreviewClickableImage from '../home/hero/PreviewClickableImage';

const CAPABILITIES = [
  'Branded colors and background',
  'Service menu with online booking',
  'Custom About and Contact pages',
  'Client login and appointment portal',
  'Deposits and card payments',
];

const Section = styled.section`
  padding: clamp(3rem, 7vw, 5rem) ${theme.spacing.xl};
  background: var(--login-form-input-bg);
  scroll-margin-top: 100px;
`;

const Row = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(2rem, 5vw, 4rem);
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  h2 {
    margin: 0 0 ${theme.spacing.md};
    font-size: clamp(1.75rem, 3vw, 2.5rem);
    font-weight: 800;
    letter-spacing: -0.03em;
    line-height: 1.15;
    color: var(--login-form-text);
  }

  > p {
    margin: 0 0 ${theme.spacing.lg};
    color: var(--login-form-muted);
    line-height: 1.65;
    font-size: ${theme.typography.fontSize.lg};
  }
`;

const CapabilityList = styled.ul`
  margin: 0 0 ${theme.spacing.xl};
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};

  li {
    padding: 8px 14px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid var(--login-form-border);
    font-size: ${theme.typography.fontSize.sm};
    font-weight: 500;
    color: var(--login-form-text);
  }
`;

const CtaWrap = styled.div`
  width: 100%;

  @media (max-width: 900px) {
    display: flex;
    justify-content: center;
  }
`;

const RoundCta = styled(WebsiteMarketingCtaButton)`
  border-radius: 999px;
  padding: 14px 28px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  background: var(--login-primary);

  &:hover:not(:disabled) {
    transform: none;
    box-shadow: none;
    background: var(--login-primary-dark);
    text-decoration: none;
  }
`;

const Visual = styled.div`
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.12);
  border: 1px solid var(--login-form-border);
  background: #fff;
`;

export default function StorefrontMarketingSection({
  id = 'storefront',
  sectionCtaLabel = 'Get Started',
  onPreviewClick,
}) {
  return (
    <Section id={id}>
      <WebsiteSectionContainer>
        <Row>
          <Copy>
            <h2>Your branded booking website</h2>
            <p>
              Give every business a professional storefront where clients browse services,
              book appointments, and manage their account — all under your own Neat Schedule link.
            </p>

            <CapabilityList aria-label="Storefront capabilities">
              {CAPABILITIES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </CapabilityList>

            <CtaWrap>
              <RoundCta as={Link} to="/main/account-setup">
                {sectionCtaLabel}
              </RoundCta>
            </CtaWrap>
          </Copy>

          <Visual>
            {onPreviewClick ? (
              <PreviewClickableImage
                src={cpPreview1}
                alt="Neat Schedule branded storefront with online booking"
                ariaLabel="View full-size storefront preview"
                onClick={() =>
                  onPreviewClick({
                    src: cpPreview1,
                    alt: 'Neat Schedule branded storefront with online booking',
                    dialogAriaLabel: 'Full-size storefront preview',
                  })
                }
              />
            ) : (
              <img src={cpPreview1} alt="Neat Schedule branded storefront with online booking" />
            )}
          </Visual>
        </Row>
      </WebsiteSectionContainer>
    </Section>
  );
}
